import * as THREE from 'three'

export type SourceVector3 = [number, number, number] | { x: number; y: number; z: number }

export function toEngineCoordinates(
  pos: SourceVector3,
  origin: SourceVector3 = [-3483.625, -3221.71875, 1183.25],
  scale = 0.05,
): THREE.Vector3 {
  const px = Array.isArray(pos) ? pos[0] : pos.x
  const py = Array.isArray(pos) ? pos[1] : pos.y
  const pz = Array.isArray(pos) ? pos[2] : pos.z

  const ox = Array.isArray(origin) ? origin[0] : origin.x
  const oy = Array.isArray(origin) ? origin[1] : origin.y
  const oz = Array.isArray(origin) ? origin[2] : origin.z

  // Source coordinate system: X=right, Y=forward/back, Z=up (right-handed:
  //   right x forward = up).
  // Three.js engine: X=right, Y=up, Z=toward viewer (right-handed), so the
  //   scene "forward" (into the screen) is -Z.
  //
  // Correct handedness-preserving map (proper rotation, Jacobian det +1):
  //   engine.x =  (source.x - ox) * scale     // right   -> right
  //   engine.y =  (source.z - oz) * scale     // up      -> up
  //   engine.z = -(source.y - oy) * scale     // forward -> -Z (into screen)
  //
  // The Z term is NEGATED: relabeling axes (x,y,z)->(x,z,y) without a sign flip
  // gives Jacobian det -1, a REFLECTION that mirrors the world (source "forward"
  // would land on engine +Z = toward the viewer, flipping front/back). Negating
  // the from-source-Y component restores a proper +90° rotation about X. This
  // matches the asset pipeline (tools/pipeline/build_gltf_assets.mjs, which bakes
  // props with the same (x, z, -y) mapping), so runtime-placed geometry (spawn,
  // room shell, sector bounds, fallback doors/crates) shares one frame with the
  // baked .glb props. Previously this used an all-positive (x, z, y) mirror,
  // which desynced runtime placement from the baked assets by the depth axis.
  const ex = (px - ox) * scale
  const ey = (pz - oz) * scale
  const ez = -(py - oy) * scale

  return new THREE.Vector3(ex, ey, ez)
}
